import { useBeforeUnloadListener } from '@/hooks/use-before-unload-listener';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useNavigationConfirmationContext } from '@/contexts/navigation-confirmation-context';

export type VoidFunction = () => void;

/*
 * Hook to prevent all sorts of navigation from screens where the user has unsaved changes.
 *
 * Uses useBeforeUnloadListener to prevent leaving the tab using browser navigation for web app
 * Uses the react-navigation beforeRemove listener to prevent leaving the screen using the device back button
 *
 * Uses NavigationConfirmationContext to prevent all other in-app manual navigation, such as clicking
 * nav links that navigates between tabs in tab stack.
 */
export const useBlockNavigation = (
  listenerMessage: string,
  confirmNavigation: (cb: VoidFunction) => void,
) => {
  const { addListener, dispatch } = useNavigation();
  const navConfirmedRef = useRef(false);
  // prevent leaving the tab on web
  useBeforeUnloadListener(listenerMessage);

  // prevent react-navigation from removing the screen (android device back button)
  useEffect(() => {
    addListener('beforeRemove', (e) => {
      if (navConfirmedRef.current) {
        // Navigation already confirmed, so continue
        return;
      }

      // Prompt the user before leaving the screen using action sheet
      e.preventDefault();

      confirmNavigation(() => {
        dispatch(e.data.action);
      });
    });
  }, [addListener, dispatch, confirmNavigation]);

  const { setNavigationBlocked } = useNavigationConfirmationContext();

  // trigger start of navigation blocking
  useEffect(() => {
    if (!navConfirmedRef.current) {
      setNavigationBlocked(true);
    }

    return () => {
      setNavigationBlocked(false);
    };
  }, [setNavigationBlocked]);

  return useCallback(() => {
    navConfirmedRef.current = true;
    setNavigationBlocked(false);
  }, [setNavigationBlocked]);
};
